<script lang="ts" setup>
import {ref, computed, onMounted, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useFeatureAccess} from '@/ts/composables/stateful/use-feature-access';
import {extractError} from '@/ts/composables/pure/use-loading-data-ref';
import LoadingError from '@/vue/templates/loading-error.vue';
import LightboxImage from '@/vue/molecules/lightbox-image.vue';
import LightboxVideo from '@/vue/molecules/lightbox-video.vue';
import BalmMediaPlayer from '@/vue/molecules/balm-media-player.vue';
import {ReccoMediaType} from '@/ts/types/component/recco/media.type';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import BackLink from '@/vue/molecules/back-link.vue';
import {pagesPath} from '@/ts/router/pages-path';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';
import LikeDislikeBlockContent from '@/vue/organisms/block-content/like-dislike-block-content.vue';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import CompanyBadge from '@/vue/atoms/company-badge.vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {contentMediaType, mediaLabelWithDuration, mediaTypeLabelKey} from '@/ts/utils/recco-label-keys';
import {useI18n} from 'vue-i18n';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import {useWatchedReport} from '@/ts/composables/stateful/use-watched-report';

const route = useRoute();
const router = useRouter();
const api = useApi();
const logger = useLogger();
const {canAccessExplore} = useFeatureAccess();
const {t: $t} = useI18n();
const viewedContent = useViewedContentStore();

const blockContent = ref<ComponentContent | null>(null);

// A piece watched here is watched, wherever else it is also offered (IPD-2980).
const {reportWatched} = useWatchedReport();
const isLoading = ref(true);
const error = ref<string | null>(null);

type ContentTab = 'description' | 'transcription';
const activeTab = ref<ContentTab>('description');
const showTabs = computed(() => Boolean(blockContent.value?.contentHTML && blockContent.value?.transcription));
const isAudioBar = computed(() => Boolean(blockContent.value?.audio && !blockContent.value?.image));

// "Listen · 12 min" above the title (IPD-3375), derived through the same precedence and helper
// as every card that links here, so the page cannot name the piece differently from the card.
const mediaLabel = computed(() => blockContent.value
  ? mediaLabelWithDuration($t(mediaTypeLabelKey[contentMediaType(blockContent.value)]), blockContent.value.duration, $t)
  : '');

async function fetchBlockContent(): Promise<void> {
  try {
    isLoading.value = true;
    error.value = null;
    const itemId = route.params.itemId as UuidDTO;
    const response = await api.me.getBlockContent(itemId);
    blockContent.value = response;
    // The backend records the VIEW on this call, so this is the moment its `consumed` flips.
    // Explore keeps its rails alive and skips the refresh on a quick back navigation, so mirror
    // the flag locally or the marker waits for a full reload (IPD-3035).
    viewedContent.markViewed(itemId);
  } catch (err) {
    logger.error('Error fetching block content:', err)();
    error.value = extractError(err, $t);
    blockContent.value = null;
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  if (!canAccessExplore.value) {
    void router.replace(pagesPath.intervention.interventionsList);
    return;
  }
  void fetchBlockContent();
});

// Watch for route param changes to refetch content when navigating between different block content items
watch(() => route.params.itemId, () => {
  void fetchBlockContent();
});

// Handle content updates from like/dislike/bookmark actions
function handleContentUpdate(updatedContent: ComponentContent): void {
  blockContent.value = updatedContent;
}

// Handle external link click
function handleExternalLinkClick(): void {
  if (blockContent.value?.externalLink) {
    window.open(blockContent.value.externalLink, '_blank');
  }
}

const labelledById = crypto.randomUUID();
</script>

<template>
  <loading-error :id="labelledById" :loading="isLoading" :error="error" type="page">
    <template #default>
      <div v-if="blockContent" class="block-content-page">
        <back-link class="back-link" :hide-arrow="true"/>

        <!-- Content Card Section -->
        <div class="content-card-wrapper">
          <q-card flat class="content-card">
            <div v-if="!isAudioBar" class="content-card-media-section">
              <lightbox-video
                v-if="blockContent.video"
                class="block-content-video"
                :video-src="calculateMediaUrl(blockContent.video)"
                :poster-src="blockContent.image ? calculateMediaUrl(blockContent.image) : undefined"
                :poster-alt="blockContent.name ?? ''"
                :is-light-boxed="blockContent.isLightBoxed"
                :title="blockContent.name ?? undefined"
                @half-watched="reportWatched(blockContent.id)"
              />
              <balm-media-player
                v-else-if="blockContent.audio && blockContent.image"
                :src="calculateMediaUrl(blockContent.audio)"
                :media-type="ReccoMediaType.Audio"
                :poster-src="calculateMediaUrl(blockContent.image)"
                :poster-alt="blockContent.name ?? ''"
                show-volume-control
                show-playback-speed
              />
              <lightbox-image
                v-else-if="blockContent.image"
                format="webp"
                :src="calculateMediaUrl(blockContent.image)"
                :width="1200"
                loading="eager"
                :alt="blockContent.name ?? ''"
                :is-light-boxed="blockContent.isLightBoxed"
                :title="blockContent.name ?? undefined"
                image-class="image"
              />
              <company-badge :company-image="blockContent.companyImage"/>
            </div>

            <q-card-section class="content-card-bottom-section">
              <span class="media-card-type">{{ mediaLabel }}</span>
              <div class="title-row">
                <span class="title">{{ blockContent.name }}</span>
                <svg-illustration class="title-arrow" name="right-down-arrow.svg" inner-class="inner-right"/>
              </div>
            </q-card-section>
          </q-card>
        </div>

        <!-- Audio bar (no poster image) -->
        <balm-media-player
          v-if="isAudioBar"
          compact
          class="audio-bar"
          :src="calculateMediaUrl(blockContent.audio!)"
          :media-type="ReccoMediaType.Audio"
          show-volume-control
          show-playback-speed
        />

        <!-- Content Body Section -->
        <div v-if="blockContent.contentHTML || blockContent.transcription" class="content-text-container">
          <!-- Tab chips when both description and transcription exist -->
          <q-tabs v-if="showTabs" v-model="activeTab" class="content-tabs" no-caps>
            <q-tab name="description" :label="$t('recco_content_description')"/>
            <q-tab name="transcription" :label="$t('recco_audio_transcription')"/>
          </q-tabs>

          <div class="text-content-wrapper">
            <div
              v-if="blockContent.contentHTML && (!showTabs || activeTab === 'description')"
              v-safe-html="blockContent.contentHTML"
              class="content-body"
              role="tabpanel"
            />

            <!-- Audio Transcription -->
            <div
              v-if="blockContent.transcription && (!showTabs || activeTab === 'transcription')"
              v-safe-html="blockContent.transcription"
              class="transcription-body"
              role="tabpanel"
            />

            <!-- External Link Button -->
            <balm-btn
              v-if="blockContent.externalLink"
              class="external-link-btn"
              :label="$t('external_link_button_text')"
              :aria-label="`${$t('external_link_button_text')} - ${blockContent.name || $t('untitled')}`"
              icon="arrow-narrow-right.svg"
              theme="blue"
              @click="handleExternalLinkClick"
            />
          </div>
        </div>

        <!-- Like/Dislike and Bookmark Section -->
        <like-dislike-block-content
          class="like-bar"
          :block-content="blockContent"
          @content-updated="handleContentUpdate"
        />
      </div>
    </template>
  </loading-error>
</template>

<style lang="scss" scoped>
.block-content-page {
  @include page-alignment(1294px);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--Spacing-spacing-4);
  padding-bottom: var(--Spacing-spacing-7);
  border-radius: var(--card-border-radius);
  margin-inline: auto;
  width: 100%;
}

.back-link.back-link {
  width: 100%;
  margin-bottom: 0;

  :deep(.link) {
    color: var(--Neutral-UI-neutral-ui-100);
    align-items: center;
  }

  :deep(.back-arrow) {
    left: 0;
  }
}

.like-bar {
  width: 100%;
}

// Content card styling (same as recco)
.content-card-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex-wrap: wrap;
  width: 100%;
}

.content-card {
  width: 100%;
  border-radius: var(--card-border-radius);
}

.content-card-media-section {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--card-border-radius) var(--card-border-radius) 0 0;
  overflow: hidden;

  .image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-top-left-radius: var(--card-border-radius);
    border-top-right-radius: var(--card-border-radius);
  }

  :deep(.lightbox-image) {
    border-radius: 0;
    border-top-left-radius: var(--card-border-radius);
    border-top-right-radius: var(--card-border-radius);
  }

  :deep(.q-skeleton) {
    border-radius: 0;
  }
}

.block-content-video {
  border-top-left-radius: var(--card-border-radius);
  border-top-right-radius: var(--card-border-radius);
  overflow: hidden;
}

.content-card-bottom-section {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-1, 8px);
  background: var(--Neutral-UI-neutral-ui-20);
  padding: var(--Spacing-spacing-6, 42px);
  align-items: flex-start;

  .media-card-type {
    @include font-caption-sm;
    color: var(--Neutral-UI-neutral-ui-70);
  }

  .title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: var(--Spacing-spacing-2);
  }

  .title {
    @include font-h1;
    color: var(--Neutral-UI-neutral-ui-100);
  }

  .title-arrow {
    display: none;

    @include screen-lg {
      display: block;
    }
  }
}

// Audio bar (compact, no poster)
.audio-bar {
  width: 100%;
  height: 64px;
  border-radius: var(--card-border-radius);
}

// Content text section (same as recco)
.content-text-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--Spacing-spacing-7) var(--Spacing-spacing-8) var(--Spacing-spacing-8) var(--Spacing-spacing-6);
  gap: var(--Spacing-spacing-4);
  flex-shrink: 0;

  @include screen-2xl {
    @include column-width(6, 10);
  }

  @include screen-xl {
    @include column-width(9, 10);
  }

  @include screen-lg {
    @include column-width(8, 10);
  }

  @include screen-md {
    @include column-width(8, 10);
  }
}

// Text content wrapper with gap
.text-content-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
}

// Content body styling (same as recco)
.content-body {
  :deep(ul) {
    padding-left: 20px;
    margin-bottom: var(--Spacing-spacing-6);
  }

  :deep(li) {
    word-break: normal;
  }

  :deep(p) {
    @include font-body;
  }
}

// Content tabs
.content-tabs {
  @include tabs;
}

.transcription-body {
  @include font-body;
  color: var(--Neutral-UI-neutral-ui-80);
}
</style>